import { useForm } from 'react-hook-form'
import { useCallback, useEffect } from 'react'
import {
  type PartnerPickDeviceAccountTypeEnum,
  type PartnerPickDevicesFormData,
} from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import {
  DeviceTypeEnum,
  usePartnerPickDeviceCreateMutation,
  usePartnerPickDeviceUpdateMutation,
} from 'domains/partner-pick-devices/api'
import {
  mapPartnerPickDeviceFormDataToCreateMutationPayload,
  mapPartnerPickDeviceFormDataToUpdateMutationPayload,
} from 'domains/partner-pick-devices/components/form/utils/PartnerPickDevicesForm.utils'

interface UsePartnerPickDeviceFormProps {
  initialFormData: PartnerPickDevicesFormData
  deviceId?: string
  warehouseLocationIdForDevicePage: string
  warehouseLocationIdForAccount: string
}

export const usePartnerPickDevicesForm = ({
  initialFormData,
  deviceId,
  warehouseLocationIdForDevicePage,
  warehouseLocationIdForAccount,
}: UsePartnerPickDeviceFormProps) => {
  const { register, unregister, watch, setValue, formState, handleSubmit, errors, clearError } =
    useForm<PartnerPickDevicesFormData>({
      defaultValues: initialFormData,
    })

  const [
    submitCreate,
    { data: deviceCreateData, loading: deviceCreateLoading, error: deviceCreateError },
  ] = usePartnerPickDeviceCreateMutation()

  const [
    submitUpdate,
    { data: deviceUpdateData, loading: deviceUpdateLoading, error: deviceUpdateError },
  ] = usePartnerPickDeviceUpdateMutation()

  useEffect(() => {
    register({ name: 'deviceType' })
    register({ name: 'accountType' })

    return () => unregister(['deviceType', 'accountType'])
  }, [register, unregister])

  const { deviceType, accountType } = watch(['deviceType', 'accountType'])

  const setDeviceType = useCallback(
    (deviceType: DeviceTypeEnum) => {
      setValue('deviceType', deviceType)

      if (deviceType === DeviceTypeEnum.Wifi) {
        if (initialFormData.deviceType === DeviceTypeEnum.Wifi) {
          setValue('phone', initialFormData.phone)
        } else {
          setValue('phone', '')
        }
        clearError('phone')
      } else {
        if (initialFormData.deviceType === DeviceTypeEnum.Phone) {
          setValue('phone', initialFormData.phone)
        } else {
          setValue('phone', '')
        }
        clearError('password')
      }
    },
    [setValue, clearError, initialFormData.phone, initialFormData.deviceType]
  )

  const setAccountType = useCallback(
    (accountType: PartnerPickDeviceAccountTypeEnum) => {
      setValue('accountType', accountType)
    },
    [setValue]
  )

  const submitForm = useCallback(
    (formData: PartnerPickDevicesFormData) => {
      if (deviceId) {
        // we only want to update fields that were changed, so compare formData against dirtFields
        const dirtyData = Object.assign(
          {},
          ...Array.from(formState.dirtyFields).map(key => ({ [key]: formData[key] }))
        )

        const variables = mapPartnerPickDeviceFormDataToUpdateMutationPayload(
          dirtyData,
          deviceId,
          warehouseLocationIdForAccount
        )

        submitUpdate({
          variables,
        })
      } else {
        const variables = mapPartnerPickDeviceFormDataToCreateMutationPayload(
          formData,
          warehouseLocationIdForDevicePage
        )

        submitCreate({
          variables,
        })
      }
    },
    [
      deviceId,
      warehouseLocationIdForDevicePage,
      formState.dirtyFields,
      submitCreate,
      submitUpdate,
      warehouseLocationIdForAccount,
    ]
  )

  return {
    register,
    handleSubmit,
    errors,
    deviceType,
    setDeviceType,
    accountType,
    setAccountType,
    submitForm,
    formSubmitData:
      deviceCreateData?.partnerPickDeviceCreate.partnerPickDevice ||
      deviceUpdateData?.partnerPickDeviceUpdate.partnerPickDevice,
    formSubmitSuccess: Boolean(deviceCreateData || deviceUpdateData),
    formSubmitLoading: deviceCreateLoading || deviceUpdateLoading,
    formSubmitError: deviceCreateError || deviceUpdateError,
  }
}
